import React, {HTMLAttributes} from 'react';
import {Provider} from "react-redux";
import {createTheme, StyledEngineProvider, Theme, ThemeProvider} from '@mui/material/styles';
import {ViewportProvider} from "./components/context/ViewportProvider.tsx";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import WordPressProvider, {WpContextData} from "./components/context/WordPressProvider.tsx";
import {store} from "./store";
import {frCA} from "date-fns/locale/fr-CA";
import {cfg} from "./config.ts";
import {ApolloProvider} from "@apollo/client/react";
import {apollo11} from "./utils/gql-client.ts";

/**
 * Material UI custom theme
 * IMPORTANT: minimalistic theme is required in WordPress setting so Elementor/theme styling (e.g. font, etc.) takes over
 */
export const xTheme: Theme = createTheme({
    typography: {
        fontFamily: [
            'EngelVoelkersText',
            'Switzer',
            'Montserrat',
            'sans serif'
        ].join(',')
    },
    // https://mui.com/material-ui/customization/breakpoints/#custom-breakpoints
    breakpoints: {
        values: {
            xs: cfg.screens.xs,
            sm: cfg.screens.sm,
            md: cfg.screens.md,
            lg: cfg.screens.lg,
            xl: cfg.screens.xl,
            "2xl": cfg.screens['2xl']
        }
    },
    palette: {
        primary: {
            light: '#242323',
            main: '#242323',
            dark: '#242323',
            contrastText: '#fff',
        },
        secondary: {
            light: '#242323',
            main: '#242323',
            dark: '#242323',
            contrastText: '#fff',
        },
        error: {
            light: '#E60000',
            main: '#E60000',
            dark: '#E60000',
            contrastText: '#fff',
        },
        info: {
            light: '#163961',
            main: '#163961',
            dark: '#163961',
            contrastText: '#fff',
        },
    },
    /**
     * Customize global styling for core MUI components
     * @source https://mui.com/material-ui/customization/theme-components/#global-style-overrides
     */
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    // borderRadius: 0
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    // borderRadius: 0
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    // borderRadius: 0
                }
            }
        }
    }
});

/**
 * Sub-app container; wrapper to avoid boilerplate
 * @param props
 * @constructor
 */
interface AppContainerProps extends HTMLAttributes<any> {
    wp: Omit<WpContextData<ZWindowData>, "wpml" | "tr">
}

// note: consider creating a different <AppContainer> for PUBLIC vs ADMIN if needed for different styling
const AppContainer = (props: AppContainerProps) => {
    return <React.StrictMode>
        <ApolloProvider client={apollo11}>
            <Provider store={store}>
                {/* https://mui.com/material-ui/guides/interoperability/#setup */}
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={xTheme}>
                        {/* https://mui.com/x/react-date-pickers/adapters-locale/#with-date-fns */}
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={frCA}>
                            <WordPressProvider wp={props.wp}>
                                <ViewportProvider>
                                    {props.children}
                                </ViewportProvider>
                            </WordPressProvider>
                        </LocalizationProvider>
                    </ThemeProvider>
                </StyledEngineProvider>
            </Provider>
        </ApolloProvider>
    </React.StrictMode>
}
export default AppContainer;
